import {LitElement, html, nothing} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {style} from './fileupload.styles';
import {SbkModal} from '../modal/modal';
import * as tus from 'tus-js-client'
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin";
import {FormValidationMixin} from "@/components/mixins/form-validation-mixin";
import {RequiredResumableUploadValidator, Validator} from "@/components/validators/validators";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {SbkLabelMixin} from "@/components/label/label-mixin.ts";
import {when} from "lit/directives/when.js";
import {labelStyles} from "@/components/label/label.styles.ts";
import {query} from "lit/decorators/query.js";


/* eslint-disable  @typescript-eslint/no-explicit-any */
declare let tx_resumableuploads: any
declare let _paq: any


@customElement('sbk-file-upload')
export class SbkFileUploadComponent extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(LitElement))) {

    static formAssociated = true;

    static shadowRootOptions = {...LitElement.shadowRootOptions, delegatesFocus: true};

    _internals: ElementInternals;

    @property({type: Boolean})
    required: boolean = false;

    @property()
    description: string = ''

    @property({type: Array})
    files: { fileName: string, status: string, message: string, progress: string, name: string, value: string }[] = [];

    @property()
    size: 'large' | 'medium' | 'small' | 'adaptive' = 'adaptive';

    @property()
    label: string = '';

    @property({attribute: 'button-label'})
    buttonLabel: string = '';

    @state()
    open = false;

    @property({type: Boolean})
    multiple: boolean = false;

    @property()
    accept: string = '';

    @property({type: String, attribute: "max-size"})
    maxSize = '10M';

    @property({type: String, attribute: "max-files"})
    maxFiles = '';

    @property()
    name: string = ''

    @property()
    value: string = ''

    @property({attribute: 'modal-heading'})
    modalHeading: string = ''

    @property({attribute: 'modal-header'})
    modalHeader: string = ''

    @property({attribute: 'modal-text'})
    modalText: string = ''

    @property({attribute: 'modal-close-text'})
    modalCloseText: string = ''

    @property({attribute: 'modal-confirm-text'})
    modalConfirmText: string = ''

    @state()
    formData: FormData = new FormData

    @query('#file-upload')
    fileUpload !: HTMLInputElement

    validatorMapper: {
        [key: string]: Validator
    } = {
        'required': RequiredResumableUploadValidator,
    }

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            this.resetValue();
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });
    }

    static get styles() {
        return [style, labelStyles];
    }

    private static arrayBufferToString(buffer: string | ArrayBuffer | null): string {
        let result = '';
        if (!(buffer instanceof ArrayBuffer)) {
            return result
        }
        const bufView = new Uint8Array(buffer);
        const length = bufView.length;
        let addition = Math.pow(2, 16) - 1;

        for (let i = 0; i < length; i += addition) {
            if (i + addition > length) {
                addition = length - i;
            }
            result += String.fromCharCode.apply(null, [...bufView.subarray(i, i + addition)]);
        }

        return result;
    }

    connectedCallback() {
        super.connectedCallback();
        this.updateFormData();
    }

    render() {
        return html`
            <fieldset class="input${this.invalid ? ' input__wrapper--error' : ''}"
                      aria-describedby="${this.name}-annotation" aria-labelledby="groupLabel">
                ${this.labelTemplate("fileupload", true, this.invalid)}
                ${when(this.description,
                        () => html`
                            <sbk-annotation id="annotation" variant="info">${unsafeHTML(this.description)}
                            </sbk-annotation>
                        `)}
                <div class="upload-wrapper">
                    <ul class="file-list">
                        ${this.files.map(({fileName, status, message, progress, name, value}, index) => html`
                            <li class="file-list-item ${this.getFileStatusClass(status)}">
                                <div class="progress-bar-inner" style="width: ${progress}%"></div>
                                <sbk-icon size="40" class="icon-status"
                                          symbol=${this.getIconForStatus(status)}></sbk-icon>
                                <p>
                                    <span class="title">${unsafeHTML(fileName)}</span>
                                    <br/>
                                    <span class="status">${unsafeHTML(message)}</span>
                                </p>
                                <slot name="modal-${index}">
                                    ${this.renderModal(index, name, status)}
                                </slot>
                                <input name=${name} .value=${value} type="hidden"/>
                            </li>
                        `)}
                    </ul>

                    <slot name="description">
                    </slot>

                    ${this.showLabel() ? html`
                        <label for="file-upload"
                               class="upload-button upload-button-${this.size} ${this.invalid ? 'upload-button--error' : ''}"
                               tabindex="0">
                            <sbk-icon size="24" symbol="upload"></sbk-icon>
                            ${this.buttonLabel}
                        </label>` : nothing}

                    <input id="file-upload" accept=${this.accept}
                           class="file-input"
                           @change=${this.handleFileUpload} name=${this.name}
                           ?required=${this.required}
                           aria-label=${this.buttonLabel} ?multiple=${this.multiple} type="file"/>

                </div>
                ${this.invalid ? this.validationErrorTemplate(`${this.name}-annotation`) : ""}
            </fieldset>
        `;
    }

    renderModal(index: number, name: string, status: string) {
        if (status === 'error') {
            return html`
                <sbk-round-button icon="close" variant="icon-only" @click=${() => {
                    this.removeFile(index, name);
                }}>X</sbk-round-button>
            `;
        }
        return html`
            <sbk-modal slot="modal-${index}" id="modal-${index}"
                       heading=${this.modalHeading} icon="warning"
                       .open=${this.open}>
                <sbk-round-button icon="close" variant="icon-only"
                                  slot="triggerElement">X
                </sbk-round-button>
                <div>
                    ${unsafeHTML(this.modalText)}
                </div>
                <sbk-button slot="footer" @click=${(e: Event) => {
                    this.removeFile(index, name);
                    this.closeModal(e, index);
                }}>${this.modalConfirmText}
                </sbk-button>
                <sbk-button slot="footer" variant="secondary"
                            @click=${(e: Event) => this.closeModal(e, index)}>
                    ${this.modalCloseText}
                </sbk-button>
            </sbk-modal>
        `;
    }

    sanitizeHTML(str: string): string {
        return str.replace(/[^\w.\-_ ]/gi, (c) => {
            return '&#' + c.charCodeAt(0) + ';';
        })
    }

    fileSizeToBytes(size: string): number {
        const units: { [key: string]: number } = {
            B: 1,
            K: 1024,
            KB: 1024,
            M: 1024 * 1024,
            MB: 1024 * 1024,
            G: 1024 * 1024 * 1024,
            GB: 1024 * 1024 * 1024,
        };

        const regex = /^(\d+(?:\.\d+)?)([BKMG]?)/i;
        const match = size.match(regex);

        if (!match) {
            throw new Error("Invalid size format");
        }

        const value = parseFloat(match[1]);
        const unit = (match[2] || "B").toUpperCase(); // Default to "B" if no unit is provided

        if (!units[unit]) {
            throw new Error(`Invalid size unit: ${unit}`);
        }

        return value * units[unit];
    }

    updateInputVisibility() {
        const inputButton: HTMLLabelElement | null = this?.renderRoot.querySelector('.upload-button');
        if (this.files.length >= 1) {
            inputButton?.classList.add('hidden')
        } else {
            inputButton?.classList.remove('hidden')
        }
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        this.formData = new FormData;
        this._internals.setFormValue('');
    }

    setValue(): void {
        this._internals.setFormValue(this.formData);
    }

    _required(): void {
        this.required = true;
        this.setAttribute('required', 'required');
    }

    optional(): void {
        this.required = false;
        this.removeAttribute('required');
        this.invalid = false;
    }

    private showLabel(): boolean {
        return !this.maxFiles || (this.files.length < Number(this.maxFiles) || !this.multiple);
    }

    private updateFileStatus(fileName: string, status: string, message: string, progress: string, name: string, value: string) {
        this.files = this.files.map(f =>
            f.fileName === fileName ? {...f, status, message, progress, name, value} : f
        );
    }

    private handleFileUpload(event: Event): void {
        const input = event.target as HTMLInputElement;
        const selectedFiles = Array.from(input.files || []);

        selectedFiles.forEach(file => {
            const isFileTooLarge = file.size > this.fileSizeToBytes(this.maxSize);
            const newFileStatus = isFileTooLarge ? 'error' : 'uploading';
            // escape filename to prevent xss
            const fileName = this.sanitizeHTML(file.name)
            const newMessage = isFileTooLarge ? this._ll('tusError.requestEntityTooLarge') + fileName : fileName + this._ll('tusError.uploadInProgress');
            this.files = [...this.files, {
                fileName: fileName,
                status: newFileStatus,
                message: newMessage,
                progress: '0',
                name: this.name,
                value: ''
            }];

            if (!isFileTooLarge) {
                this.uploadFile(file, fileName);
            } else {
                this.updateFileStatus(fileName, 'error', this._ll('tusError.requestEntityTooLarge') + fileName, '0', '', '');
            }
        });

        const validators = this.getValidators();
        this.handleErrors(validators, '');
    }

    private uploadFile(file: File, fileName: string): void {
        // Create a new tus upload
        const upload = new tus.Upload(file, {
            endpoint: '/files/',
            retryDelays: [0, 1000, 3000, 5000],
            chunkSize: 1000000,
            metadata: {
                filename: file.name,
                filetype: file.type
            },
            onError: (error) => {
                this.updateFileStatus(fileName, 'error', `${this.getUploadErrorMessage(error.message, fileName)}`, '0', '', '');
            },
            onProgress: (bytesUploaded, bytesTotal) => {
                const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2)
                this.updateFileStatus(fileName, 'uploading', this._ll('tusStatus.uploadInProgress'), percentage, this.name, '');
            },
            onSuccess: () => {
                let inputName = '';
                const value = 'url:' + upload.url;
                if (this.multiple) {
                    inputName = this.name.replace('[]', '[_uploadedFiles][]');
                } else {
                    inputName = this.name + '[_uploadedFiles][]';
                    this.updateInputVisibility();
                }
                this.updateFileStatus(fileName, 'success', this._ll('tusStatus.uploadSuccess'), '100', inputName, value);

                this.formData.append(inputName, value);
                this._internals.setFormValue(this.formData);
            }
        });

        // Use FileReader to check if file is encrypted or has a digital signature
        const fileReader: FileReader = new FileReader();
        fileReader.readAsArrayBuffer(file);
        fileReader.onloadend = function () {
            const result = SbkFileUploadComponent.arrayBufferToString(fileReader.result);

            if (result.indexOf('/Encrypt') !== -1) {
                _paq.push(['trackEvent', 'Upload geschützte pdf-Datei', 'Grund Berechtigungen eingeschränkt', 'Grund Berechtigungen eingeschränkt']);
                upload?.options?.onError?.(new Error("encrypted"));
            } else if (result.indexOf('/Type/Sig') !== -1) {
                _paq.push(['trackEvent', 'Upload geschützte pdf-Datei', 'Grund Digitale ID', 'Grund Digitale ID']);
                upload?.options?.onError?.(new Error("withSignature"));
            } else {
                upload.start();
            }
        }
    }

    private removeFile(index: number, name: string): void {
        this.fileUpload.value = '';
        this.files = this.files.filter((_, i) => i !== index);
        this.formData.delete(name);
        this.files.forEach((file) => {
            this.formData.append(file.name, file.value);
        })
        this._internals.setFormValue(this.formData);
        if (!this.multiple) {
            this.updateInputVisibility();
        }
    }

    private closeModal(e: Event, index: number): void {
        const modal: SbkModal | null = this.renderRoot.querySelector(`#modal-${index}`);
        modal?._closeModal(e)
    }

    private getFileStatusClass(status: string): string {
        return {
            'uploading': 'file-uploading',
            'error': 'file-error',
            'success': 'file-success'
        }[status] || '';
    }

    private getIconForStatus(status: string): string {
        return {
            'uploading': 'upload',
            'error': 'warning',
            'success': 'checkmark'
        }[status] || '';
    }

    private _ll(key: string): string {
        return typeof tx_resumableuploads !== 'undefined' ? tx_resumableuploads.lang[key] : key;
    }

    private getUploadErrorMessage(errorMessage: string, fileName: string): string {
        if (0 <= errorMessage.indexOf('response code: 413')) {
            return this._ll('tusError.requestEntityTooLarge') + fileName;
        } else if (0 <= errorMessage.indexOf('response code: 415')) {
            return this._ll('tusError.unsupportedMediaType') + fileName;
        } else if (0 <= errorMessage.indexOf('response code: 422')) {
            return this._ll('tusError.unprocessableEntity').replace('%1$s', fileName);
        } else if (errorMessage === 'encrypted') {
            return this._ll('tusError.encrypted') + fileName;
        } else if (errorMessage === 'withSignature') {
            return this._ll('tusError.withSignature') + fileName;
        } else if (errorMessage === 'extension') {
            return this._ll('tusError.extensionManipulated') + fileName;
        }
        return this._ll('tusError.badRequest') + fileName;
    }

    private updateFormData(): void {
        this.files.forEach((file) => {
            this.formData.append(file.name, file.value)
        })
        this._internals.setFormValue(this.formData);
    }
}
